import React, { useEffect, useState, useCallback } from 'react';
import crmClient from './crmClient';
import { formatDateTime, formatNumber } from './format';

const FILTERS = [
  { key: 'hot', label: '🔥 Hot leads' },
  { key: 'company', label: 'Companies' },
  { key: 'identified', label: 'Identified' },
  { key: 'all', label: 'All visitors' }
];

const sourceLabel = v => v.utm_source || (v.first_referrer ? v.first_referrer.replace(/^https?:\/\//, '').split('/')[0] : 'Direct');

// Local-time boundaries for a named date range → { since, until } ISO strings.
function dateWindow(range) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === 'today') return { since: startOfToday.toISOString(), until: null };
  if (range === 'yesterday') {
    const y = new Date(startOfToday);
    y.setDate(y.getDate() - 1);
    return { since: y.toISOString(), until: startOfToday.toISOString() };
  }
  if (range === 'week') {
    const w = new Date(startOfToday);
    w.setDate(w.getDate() - 6);
    return { since: w.toISOString(), until: null };
  }
  if (range === 'month') {
    const m = new Date(startOfToday);
    m.setDate(m.getDate() - 29);
    return { since: m.toISOString(), until: null };
  }
  return { since: null, until: null };
}

const DATE_RANGES = [
  { key: 'all', label: 'Any time' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'Last 30 days' }
];

const VisitorsView = () => {
  const savedFilters = (() => {
    try {
      return JSON.parse(window.localStorage.getItem('crm_visitors_filters') || '{}') || {};
    } catch (e) {
      return {};
    }
  })();
  const [filter, setFilter] = useState(savedFilters.filter || 'hot');
  const [range, setRange] = useState(savedFilters.range || 'all');
  const [visitors, setVisitors] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  // Persist the active filters so they survive refresh / re-login.
  useEffect(() => {
    try {
      window.localStorage.setItem('crm_visitors_filters', JSON.stringify({ filter, range }));
    } catch (e) {
      /* ignore storage errors */
    }
  }, [filter, range]);

  const load = useCallback(() => {
    setLoading(true);
    const win = dateWindow(range);
    crmClient
      .listVisitors({ filter, since: win.since, until: win.until })
      .then(payload => {
        setLoading(false);
        setVisitors((payload && payload.visitors) || []);
        setTotal((payload && payload.total) != null ? payload.total : ((payload && payload.visitors) || []).length);
      })
      .catch(() => setLoading(false));
  }, [filter, range]);

  useEffect(() => {
    load();
  }, [load]);

  const openVisitor = id => {
    crmClient.getVisitor(id).then(payload => {
      setSelected((payload && payload.visitor) || null);
      setEvents((payload && payload.events) || []);
    });
  };

  const addLead = visitor => {
    setBusy(true);
    crmClient
      .addVisitorLead(visitor.crm_visitor_id, {})
      .then(() => {
        setBusy(false);
        setNotice(`${visitor.company_name || visitor.visitor_name || 'Visitor'} added to the pipeline as a prospect.`);
        load();
        setSelected(null);
      })
      .catch(() => setBusy(false));
  };

  const ignore = visitor => {
    crmClient.ignoreVisitor(visitor.crm_visitor_id).then(() => {
      load();
      setSelected(null);
    });
  };

  const markAllReviewed = () => {
    setBusy(true);
    crmClient
      .markVisitorsReviewed()
      .then(res => {
        setBusy(false);
        const n = res && res.reviewed != null ? res.reviewed : 0;
        setNotice(`Marked ${n} hot lead${n === 1 ? '' : 's'} as reviewed — the badge is cleared. They stay in your list.`);
        load();
      })
      .catch(() => setBusy(false));
  };

  const identity = v => v.visitor_name || v.visitor_email || v.company_name || 'Anonymous visitor';

  return (
    <div className="crm-visitors">
      <div className="crm-filters">
        {FILTERS.map(f => (
          <button key={f.key} type="button" className={`crm-btn crm-btn--sm ${filter === f.key ? '' : 'crm-btn--ghost'}`} onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
        <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" onClick={load}>Refresh</button>
        <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" disabled={busy} onClick={markAllReviewed} title="Clears the Visitors badge; keeps hot leads in the list">Mark all reviewed</button>
        <span className="crm-visitor-total" style={{ marginLeft: 'auto' }}>
          <strong>{formatNumber(total)}</strong> visitor{total === 1 ? '' : 's'} · {(DATE_RANGES.find(r => r.key === range) || {}).label || 'All time'}
        </span>
        <select className="crm-btn crm-btn--sm crm-btn--ghost" value={range} onChange={e => setRange(e.target.value)}>
          {DATE_RANGES.map(r => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>
      </div>
      {notice && <div className="crm-preview-banner">{notice}</div>}
      <p className="crm-muted" style={{ fontSize: 12 }}>
        Company reveal identifies which business is visiting (works for corporate networks). Person details fill in when a visitor chats, fills a form, or signs up.
      </p>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Visitor / Company</th>
              <th>Location</th>
              <th>Source</th>
              <th>Views</th>
              <th>Last seen</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="crm-muted">Loading…</td></tr>
            )}
            {!loading && visitors.length === 0 && (
              <tr><td colSpan={7} className="crm-muted">No visitors yet.</td></tr>
            )}
            {visitors.map(v => (
              <tr key={v.crm_visitor_id} className={selected && selected.crm_visitor_id === v.crm_visitor_id ? 'crm-row-active' : ''}>
                <td onClick={() => openVisitor(v.crm_visitor_id)} style={{ cursor: 'pointer' }}>
                  <strong>{identity(v)}</strong>
                  {v.company_name && v.visitor_name && <small className="crm-muted"> · {v.company_name}</small>}
                  {v.visitor_email && <small className="crm-cell-address">{v.visitor_email}</small>}
                  {v.visitor_phone && <small className="crm-cell-address">📞 {v.visitor_phone}</small>}
                </td>
                <td>{[v.city, v.region, v.country].filter(Boolean).join(', ') || '—'}</td>
                <td>{sourceLabel(v)}</td>
                <td>{formatNumber(v.pageview_count)}</td>
                <td>{formatDateTime(v.last_seen_at)}</td>
                <td>
                  {v.is_hot_lead && v.lead_status !== 'added' && v.lead_status !== 'ignored' ? (
                    <span className="crm-stage-pill crm-stage-pill--amber">🔥 {v.hot_reason === 'investor' ? 'Investor' : 'Hot'}</span>
                  ) : v.lead_status === 'added' ? (
                    <span className="crm-stage-pill crm-stage-pill--green">In CRM</span>
                  ) : v.lead_status === 'ignored' ? (
                    <span className="crm-stage-pill crm-stage-pill--slate">Ignored</span>
                  ) : v.is_identified ? (
                    <span className="crm-stage-pill crm-stage-pill--blue">Identified</span>
                  ) : v.is_company_identified ? (
                    <span className="crm-stage-pill crm-stage-pill--teal">Company</span>
                  ) : (
                    <span className="crm-stage-pill crm-stage-pill--slate">Anonymous</span>
                  )}
                </td>
                <td>
                  {v.lead_status !== 'added' && (v.company_name || v.visitor_email) && (
                    <button type="button" className="crm-btn crm-btn--sm" disabled={busy} onClick={() => addLead(v)}>Add lead</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="crm-modal" role="dialog" aria-modal="true">
          <div className="crm-modal-card crm-prospect-detail">
            <div className="crm-detail-head">
              <div>
                <h3>{identity(selected)}</h3>
                <p className="crm-muted">
                  {[selected.city, selected.region, selected.country].filter(Boolean).join(', ') || 'Location unknown'}
                  {selected.org ? ` · ${selected.org}` : ''}
                </p>
              </div>
              <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" onClick={() => setSelected(null)}>Close</button>
            </div>

            <div className="crm-field-row">
              {selected.company_name && <span>🏢 {selected.company_name}{selected.company_domain ? ` (${selected.company_domain})` : ''}</span>}
              {selected.visitor_email && <span>✉️ {selected.visitor_email}</span>}
              {selected.visitor_phone && <span>📞 {selected.visitor_phone}</span>}
            </div>
            <div className="crm-field-row">
              <span>Source: {sourceLabel(selected)}</span>
              {selected.utm_campaign && <span>Campaign: {selected.utm_campaign}</span>}
              <span>{formatNumber(selected.pageview_count)} views · {formatNumber(selected.session_count)} sessions</span>
            </div>

            <h4>Pages viewed</h4>
            <ul className="crm-visitor-events">
              {events.map(e => (
                <li key={e.crm_visitor_event_id}>
                  <span className="crm-muted">{formatDateTime(e.occurred_at)}</span> — {e.page_title || e.page_url}
                </li>
              ))}
              {events.length === 0 && <li className="crm-muted">No page events.</li>}
            </ul>

            <div className="crm-detail-actions">
              {selected.lead_status !== 'added' && (
                <button type="button" className="crm-btn" disabled={busy} onClick={() => addLead(selected)}>Add to pipeline as prospect</button>
              )}
              {selected.lead_status !== 'ignored' && selected.lead_status !== 'added' && (
                <button type="button" className="crm-btn crm-btn--ghost" onClick={() => ignore(selected)}>Ignore</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorsView;
